import { Alert, Stack } from '@mui/material'
import MDButton from '../MDButton'
import StepHeader from './StepHeader'
import ContactDetailsSection from '../contacts/ContactDetailsSection'
import useMarketingDecision, { withDecision } from '../consent/useMarketingDecision'
import { withFlag } from '../contacts/contactLists'

export const NO_RECIPIENTS_MESSAGE = 'ללקוח זה אין כתובת דוא"ל המאושרת לדיוור.'
export const EMAILS_HEADER = 'לאיזו כתובת תרצו לשלוח את המודעה?'
export const NO_EMAILS_MESSAGE = 'יש להוסיף כתובת דוא"ל ולאשר אותה לדיוור'
export const RECIPIENTS_NEXT_HINT = 'לחצו על הבא כדי לבחור את שם השולח'
export const NO_COMPANY_MESSAGE = 'יש לבחור חברה'
export const NO_COMPANY_LINK = 'לבחירת חברה'

export default function SendRecipientStep({
    belongsToFirm,
    selectedCompanyId,
    onGoToCompany,
    emails,
    onDetailsChanged,
    sendableIds,
    selectedIds,
    onToggle,
    onApproved,
}) {
    const hasCompany = selectedCompanyId != null
    const showsEmails = belongsToFirm && hasCompany
    const hasEmailRows = emails.items.some((detail) => detail.type === 'email')
    const showsNoApprovedWarning = showsEmails && hasEmailRows && sendableIds.length === 0

    const storeFlagChange = async (id, key, wanted) => {
        const detail = emails.items.find((item) => item.id === id)
        const flags = withFlag(detail?.flags, key, wanted)
        const outcome = await emails.update(id, { flags })

        if (outcome.refused != null) {
            throw outcome.refused
        }
    }

    const applyDecision = async (decided) => {
        emails.setItems((current) => withDecision(current, decided))

        const approved = (decided?.flags ?? []).includes('marketing')
        if (approved) {
            onApproved?.(decided.id)
        }

        await onDetailsChanged?.()
    }

    const marketing = useMarketingDecision({
        details: emails.items,
        consentPathOf: (detailId) => `/companies/${selectedCompanyId}/details/${detailId}/consent`,
        onDecided: applyDecision,
        onFlagChange: storeFlagChange,
    })

    const addEmail = (input) => emails.create({
        ...input,
        flags: [],
    })

    const updateEmail = ({ id, ...input }) => emails.update(id, input)

    const removeMarketingEmail = (id) => {
        emails.remove(id)
        return emails.confirmRemoval(id)
    }

    if (!hasCompany) {
        return (
            <Alert
                severity="info"
                data-testid="recipients-no-company"
                action={
                    <MDButton
                        variant="outlined"
                        color="info"
                        data-testid="recipients-no-company-link"
                        onClick={onGoToCompany}
                    >
                        {NO_COMPANY_LINK}
                    </MDButton>
                }
            >
                {NO_COMPANY_MESSAGE}
            </Alert>
        )
    }

    return (
        <Stack spacing={2}>
            {showsEmails && <StepHeader>{EMAILS_HEADER}</StepHeader>}

            {selectedIds.length > 0 && (
                <Alert severity="info" data-testid="next-step-hint">{RECIPIENTS_NEXT_HINT}</Alert>
            )}

            {showsNoApprovedWarning && (
                <Alert severity="warning" data-testid="recipients-no-approved">{NO_RECIPIENTS_MESSAGE}</Alert>
            )}

            {showsEmails && (
                <ContactDetailsSection
                    emptyMessage={NO_EMAILS_MESSAGE}
                    details={emails.items}
                    onAdd={addEmail}
                    onUpdate={updateEmail}
                    onRemove={emails.remove}
                    onRemoveMarketing={removeMarketingEmail}
                    onRestore={emails.restore}
                    onConfirmRemoval={emails.confirmRemoval}
                    refusals={emails.refusals}
                    onFlagChange={marketing.onFlagChange}
                    flags={['marketing']}
                    busy={marketing.deciding}
                    fixedType="email"
                    reorderable={false}
                    selection={{
                        kind: 'checkbox',
                        selectedIds,
                        selectableIds: sendableIds,
                        onSelect: onToggle,
                    }}
                />
            )}

            {marketing.dialog}
        </Stack>
    )
}
